import { Dispatch, useCallback, useEffect, useRef, useState } from "react";

// T4.5 — "tem que mudar esses icones, eles estao muito antigos". Eram dez PNGs de ~40px, cada um
// com fundo de uma cor propria e arbitraria, e borrados em monitor 2x. Viraram um conjunto em SVG,
// com a cor do tile agrupando por FUNCAO. O porque de cada escolha esta em `icones-do-editor.tsx`.
import {
  IconePersonagens,
  IconeCenarios,
  IconeTextos,
  IconeFiguras,
  IconeHistorico,
  IconeInserirQuadro,
  IconeRedimensionar,
  IconeExportarPdf,
  IconeLimpar,
  IconeSalvar,
} from "./icones-do-editor";

import Submenu from "./submenu";
import { Scenario } from "../../classes/scenario.class";
import { Rectangle } from "../../classes/rectangle.class";
import { Character } from "../../classes/character.class";
import { TextBubble } from "../../classes/text-bubble.class";
import { Comic, ElType } from "../../classes/comic.class";

import "./criar-comic.scss";
import GenerateImage from "./generate-image";
import { MetadadosDaImagem } from "../../api/app-user.class";
import ImageHistory from "./image-history";
import ConfirmPopUp, { PopUpInfo } from "../../components/confirm-pop-up";
import Nav from "../../components/nav";
import { GlobalState } from "../../global-state-context";
import { Figure } from "../../classes/figure.class";
import {
  PADROES_PAGINA,
  FormatoPagina,
  RETICULAS,
  ROTULO_RETICULA,
} from "../../api/documento.types";
import { useTituloDaAba } from "../../hooks/use-titulo-da-aba";
import { ehEstiloDeBalao, urlDoBalao } from "../../classes/balao-do-estilo";
import { PERGUNTA_DO_BALAO } from "../../classes/editar-texto-do-balao";
import ReguaDoSalvamento, { EstadoDoSalvamento } from "./regua-do-salvamento";

export type ImgInfo = {
  id: string;
  type: ElType;
};

type propsType = {
  globalState: GlobalState;
  currentComic: Comic;
  setCurrentComic: Dispatch<Comic | null | undefined>;
  /**
   * CHAMADO NA PRIMEIRA VEZ QUE O QUADRINHO GANHA ID — T8.20.
   *
   * Quem sabe trocar a URL é a rota, não o editor: o editor não conhece o endereço em que está montado,
   * e dar-lhe o `navigate` o faria conhecer. Então ele avisa, e a rota decide.
   *
   * Opcional porque a prévia e a vitrine montam este componente sem rota por trás.
   */
  aoGanharId?: (id: string) => void;
};

function CreateComic({
  globalState,
  currentComic,
  setCurrentComic,
  aoGanharId,
}: propsType) {
  const [comic, setComic] = useState(currentComic);
  const [selectedPageNum, setSelectedPageNum] = useState(0);

  /**
   * O DESFAZER (C3 da especificacao 002).
   *
   * A pilha guarda o retrato ANTERIOR a cada alteracao, e quem tira o retrato e o proprio
   * `Comic` — ver `registrarAntes` la. Aqui so se empilha e se desempilha.
   *
   * O teto de 20 nao e economia de memoria: e a promessa que a tela faz. Um desfazer que
   * finge lembrar de tudo e pior do que um que diz ate onde vai.
   */
  const [pilhaDesfazer, setPilhaDesfazer] = useState<Comic[]>([]);

  // `useCallback` com lista vazia e atualizacao funcional: a identidade nunca muda, entao o
  // gancho que o `Comic` guarda no primeiro render continua valido, e ainda assim nunca le
  // estado velho.
  const registrarAlteracao = useCallback((anterior: Comic) => {
    setPilhaDesfazer((pilha) => [...pilha.slice(-19), anterior]);
  }, []);

  const [caixaDaFolha, areaDaFolha] = useTamanhoDaCaixa();
  // `null` = ajustar sozinho. Um numero = a pessoa escolheu, e a escolha dela manda.
  const [zoomEscolhido, setZoomEscolhido] = useState<number | null>(null);
  const [selectedSubmenu, setSelectedSubmenu] = useState<ElType | null>(null);
  const [isGenerateImgOpen, setIsGenerateImgOpen] = useState(false);
  const [isImgHistoryOpen, setIsImgHistoryOpen] = useState(false);
  const [popUpInfo, setPopUpInfo] = useState<PopUpInfo | null>(null);
  const [mostrarEstilosDeQuadro, setMostrarEstilosDeQuadro] = useState(false);
  // Dois painéis separados de propósito: "Redimensionar" muda a página que está aberta e
  // "Adicionar página" cria outra. Um estado só faria o painel aberto por um botão responder ao
  // clique do outro, e a ação errada num editor é mais caro que um `useState` a mais.
  const [mostrarTamanhos, setMostrarTamanhos] = useState(false);
  const [mostrarReticula, setMostrarReticula] = useState(false);
  const [mostrarTamanhosNovaPagina, setMostrarTamanhosNovaPagina] =
    useState(false);

  /**
   * SALVAR SOZINHO, E DIZER QUE SALVOU — `T8.3` e `T8.28`.
   *
   * O achado, na letra: *"Adicionei um balão a um quadrinho já salvo e cliquei em 'Meus
   * quadrinhos' no menu. Não houve nenhuma pergunta do tipo 'Sair sem salvar?'. Ao reabrir o
   * quadrinho, o balão não estava lá. Para crianças, que raramente lembram de salvar, isso é
   * perda de trabalho."* E a sugestão: *"Salvar o rascunho a cada alteração (ou a cada poucos
   * segundos) e mostrar 'Salvo há 5 s'."*
   *
   * ── TRÊS DECISÕES QUE O CÓDIGO ABAIXO NÃO CONTA ─────────────────────────────────────────────
   *
   * 1. **Só salva sozinho quadrinho que JÁ TEM NOME.** Um quadrinho novo não tem nome, e salvar
   *    sozinho exigiria inventar um ("Sem título 3") ou abrir o diálogo do nome sem ninguém ter
   *    pedido. Os dois são piores que não salvar: o primeiro enche o acervo de lixo com nome de
   *    máquina, o segundo interrompe quem está desenhando. Para esse caso a régua diz **"Ainda não
   *    salvo"**, que é a verdade, e o botão Salvar continua ali.
   *
   * 2. **Espera 3 segundos de quietude, e não salva a cada alteração.** Arrastar um quadro dispara
   *    `registrar()` a cada quadro de animação; salvar em todos seria uma escrita por pixel de
   *    arrasto, num navegador de celular de escola. Três segundos é depois de a mão parar.
   *
   * 3. **Se a escrita falhar, a régua diz que falhou** — e o contador do `Comic` não é zerado pelo
   *    `catch`, porque o `save()` zera antes de escrever de propósito (está escrito lá). Então a
   *    próxima tentativa acontece na alteração seguinte, sozinha.
   */
  const [salvamento, setSalvamento] = useState<EstadoDoSalvamento>(() => (currentComic.getId() ? { estado: "salvo", quando: Date.now() } : { estado: "nunca" }));

  useEffect(() => {
    const userId = globalState.appUser?.Id();
    // Sem conta, sem nome ou sem id: não há para onde salvar sozinho. Ver a decisão 1.
    if (!userId || !comic.getId() || !comic.Name()) return;
    if (!comic.temAlteracaoNaoSalva()) return;

    const relogio = setTimeout(() => {
      setSalvamento({ estado: "salvando" });
      comic
        .save(userId)
        .then(() => setSalvamento({ estado: "salvo", quando: Date.now() }))
        .catch((erro) => {
          console.log(erro);
          setSalvamento({ estado: "falhou" });
        });
    }, 3000);
    return () => clearTimeout(relogio);
    // T20 · `pilhaDesfazer` entrou aqui em 12/10. Sem ela, o efeito só rodava quando o `Comic`
    // INTEIRO era trocado (`setComic`): redimensionar, mover ou reescrever um balão chama
    // `registrar()`, que empilha no desfazer e não troca o `Comic` — e o salvamento sozinho
    // nunca rearmava. Medido no ar: a régua seguiu em "Salvo há 1 min" depois de redimensionar.
    // Cada alteração empilha um retrato, então a pilha é o sinal de "a mão mexeu", e o
    // `clearTimeout` acima faz os 3 s contarem do ÚLTIMO gesto, que é a decisão 2.
  }, [comic, globalState.appUser, pilhaDesfazer]);

  // A régua "Salvo há 5 s" conta sozinha DENTRO DELA (`regua-do-salvamento.tsx`). Até o T20 o
  // relógio estava aqui e redesenhava o editor inteiro a cada segundo — e com ele remontava cada
  // item da folha, o que derrubava o redimensionamento no meio do gesto. Ver o cabeçalho de lá.

  /**
   * AS DUAS SAÍDAS QUE O SALVAMENTO DE 3 SEGUNDOS NÃO COBRE — `T8.3` e `T2.44`.
   *
   * O achado descreve exatamente o intervalo que sobra: *"Adicionei um balão e cliquei em 'Meus
   * quadrinhos'. Não houve nenhuma pergunta do tipo 'Sair sem salvar?'."* Entre a alteração e os 3
   * segundos de quietude há uma janela, e é dentro dela que a pessoa clica.
   *
   * SÃO DUAS SAÍDAS DIFERENTES E A RESPOSTA CERTA É DIFERENTE EM CADA:
   *
   * 1. **Sair para outra tela do produto** (o clique em "Meus quadrinhos"). Aqui dá para salvar, e
   *    salvar é melhor que perguntar: perguntar põe a criança diante de uma decisão que ela não
   *    tem como avaliar, e a resposta errada custa o trabalho dela. Então o editor **salva ao ser
   *    desmontado**, sem diálogo nenhum.
   *
   * 2. **Fechar a aba ou recarregar.** Aqui não dá para salvar — o navegador não espera escrita de
   *    rede ao fechar, e prometer que salvou seria mentir. O que dá é **avisar**, e é para isso
   *    que `beforeunload` existe. O texto é do navegador; o nosso papel é só dizer que há o que
   *    perder.
   *
   * O `ref` existe porque a limpeza do `useEffect` fecha sobre o valor do render em que foi
   * criada. Com `[comic]` nas dependências, ela rodaria a cada alteração — salvando o tempo todo.
   * Com `[]` e sem `ref`, ela salvaria o `comic` do primeiro render, que não tem as alterações.
   */
  const comicAgora = useRef(comic);
  comicAgora.current = comic;
  const usuarioAgora = useRef(globalState.appUser?.Id());
  usuarioAgora.current = globalState.appUser?.Id();

  useEffect(() => {
    const avisarAoFechar = (e: BeforeUnloadEvent) => {
      if (!comicAgora.current.temAlteracaoNaoSalva()) return;
      e.preventDefault();
      // Navegadores modernos ignoram o texto e mostram o deles. O `returnValue` continua sendo o
      // que liga o aviso, e é por isso que ele está aqui mesmo sem ninguém ler a frase.
      e.returnValue = "Você tem alterações que ainda não foram salvas.";
    };
    window.addEventListener("beforeunload", avisarAoFechar);

    return () => {
      window.removeEventListener("beforeunload", avisarAoFechar);
      const c = comicAgora.current;
      const uid = usuarioAgora.current;
      // Mesmas três condições do salvamento automático: sem conta, sem id ou sem nome não há para
      // onde salvar, e inventar nome na saída seria pior que não salvar.
      if (!uid || !c.getId() || !c.Name() || !c.temAlteracaoNaoSalva()) return;
      // Sem `await` e sem `setSalvamento`: o componente está saindo, e um `setState` depois da
      // saída é aviso no console sem nada de útil. A escrita continua e termina sozinha.
      c.save(uid).catch((erro) => console.log(erro));
    };
  }, []);


  // T8.39 · o editor dizia so "HQ-Lab" na aba, medido em 26/09. Aqui o nome da tela e o nome do
  // QUADRINHO: quem tem duas historias abertas precisa saber qual e qual, e "Editor · HQ-Lab" nas
  // duas abas nao resolve nada. Sem nome ainda, o hook devolve so o nome do produto.
  useTituloDaAba(comic.Name() || undefined);

  /**
   * ONDE O ELEMENTO CAI — `T8.10` e `T8.33`.
   *
   * O achado: *"Clicar num balão ou num personagem o coloca sempre no canto superior esquerdo da
   * página, pequeno e metade sobre a borda do primeiro quadro."* Era literal: todos os `add*`
   * criavam o item com `x: 20, y: 20`, e 20 px do canto de uma folha A4 de 1240×1754 cai **em cima
   * da borda** do primeiro quadro da grade.
   *
   * A sugestão diz para onde: *"Colocar no centro do quadro SELECIONADO (ou da área visível)."*
   *
   * ── A ORDEM DAS TRÊS TENTATIVAS, e ela é a sugestão na letra ────────────────────────────────
   *
   * 1. **o quadro selecionado**, se há um. É a intenção mais provável: quem acabou de clicar num
   *    quadro quer pôr algo nele.
   * 2. **o primeiro quadro da página**, se não há seleção. Numa grade pronta, é onde a história
   *    começa — e cai dentro de um quadro, não sobre a borda dele.
   * 3. **o centro da folha**, se a página não tem quadro nenhum (a "grade livre" e a "capa" nascem
   *    sem grade, de propósito).
   *
   * E o item é centrado pelo PRÓPRIO tamanho, não encostado no canto do quadro: `- largura / 2`.
   * Sem isso, "no centro" põe o canto superior esquerdo do item no centro, e metade dele fica fora.
   */
  function ondeColocar(largura: number, altura: number) {
    const pagina = comic.getPage(selectedPageNum);
    const selecionado = comic.SelectedItem();
    const quadros = pagina.items.filter((i) => i instanceof Rectangle);

    const alvo =
      selecionado instanceof Rectangle
        ? selecionado
        : quadros.length
        ? quadros[0]
        : null;

    const cx = alvo ? alvo.x + alvo.width / 2 : pagina.width / 2;
    const cy = alvo ? alvo.y + alvo.height / 2 : pagina.height / 2;

    // `Math.max(0, …)`: um quadro menor que o item daria coordenada negativa, e item fora da folha
    // é pior que item no canto — some da tela e a pessoa acha que o clique não funcionou.
    return {
      x: Math.round(Math.max(0, cx - largura / 2)),
      y: Math.round(Math.max(0, cy - altura / 2)),
    };
  }

  function addCharacter(imgId: string) {
    const newCharacter = new Character(
      {
        id: "",
        // T8.33 · era `x: 20, y: 20` — o canto, por cima da borda do primeiro quadro.
        ...ondeColocar(150, 150),
        width: 150,
        height: 150,
        rotation: 0,
        mirror: false,
        imgId,
      },
      globalState.appUser?.getImgUrl(imgId) || ""
    );
    newCharacter.waitImgLoad().then(() => {
      comic.addItem(selectedPageNum, newCharacter);
      setComic(comic.copy());
    });
  }
  function addScenario(imgId: string) {
    const newScenario = new Scenario(
      {
        id: "",
        // T8.33 · era `x: 20, y: 20` — o canto, por cima da borda do primeiro quadro.
        ...ondeColocar(150, 150),
        width: 150,
        height: 150,
        rotation: 0,
        mirror: false,
        imgId,
      },
      globalState.appUser?.getImgUrl(imgId) || ""
    );
    newScenario.waitImgLoad().then(() => {
      comic.addItem(selectedPageNum, newScenario);
      setComic(comic.copy());
    });
  }

  /**
   * O BALÃO ENTRA NA PÁGINA — e o primeiro argumento é uma de duas coisas.
   *
   * `T8.9` fez a prateleira "Textos" oferecer os cinco estilos desenhados (`balao-do-estilo.ts`),
   * então `origem` pode ser:
   *
   *   · um **estilo** (`"fala"`, `"grito"`…) — a forma é SVG, desenhada aqui, e não existe no acervo;
   *   · um **id de imagem da conta** — o caminho antigo, de balão salvo pela pessoa.
   *
   * As duas terminam no mesmo lugar: uma URL de imagem. Distinguir na entrada e não no meio é o que
   * mantém um caminho só a partir daqui.
   */
  function addTextBubble(origem: string, text?: string) {
    const urlDaForma = ehEstiloDeBalao(origem)
      ? urlDoBalao(origem)
      : globalState.appUser?.getImgUrl(origem) || "";
    text = text?.trim() || "";
    if (!text) {
      setPopUpInfo({
        // A MESMA frase do clique duplo (`editar-texto-do-balao.ts`). Eram duas — "Insira o texto do
        // balao" ao inserir e outra ao editar —, e a mesma caixa com duas perguntas diferentes faz a
        // pessoa achar que sao duas coisas.
        text: PERGUNTA_DO_BALAO,
        type: "input",
        close: () => setPopUpInfo(null),
        confirm: (input) => {
          if (!!input) {
            addTextBubble(origem, input);
          }
          setPopUpInfo(null);
        },
        showMathKeyboard: true,
      });
      return;
    }

    if (text.length >= 100) {
      // T8.31 · o modal do produto no lugar do `alert()` do navegador.
      setPopUpInfo({
        type: "alert",
        text: "O texto do balão passou de 100 letras. Encurte um pouco e tente de novo.",
        close: () => setPopUpInfo(null),
        confirm: () => setPopUpInfo(null),
      });
      return;
    }

    const newTextBubble = new TextBubble(
      {
        id: "",
        // T8.33 · era `x: 20, y: 20` — o canto, por cima da borda do primeiro quadro.
        ...ondeColocar(150, 150),
        width: 150,
        height: 150,
        rotation: 0,
        mirror: false,
        // O `imgId` do balão desenhado é o próprio nome do estilo. Ele é o que fica GRAVADO no
        // documento, e é por isso que ele não pode ser a URL: a URL tem 900 letras de SVG, e
        // guardá-la por balão engordaria o documento sem acrescentar nada que `urlDoBalao` não
        // saiba redesenhar.
        imgId: origem,
        text,
        fontSize: 16,
        // Era "Arial", e Arial nao esta no manual de marca (medido em `rastro/varrer-marca.mjs`).
        // A do texto e Fredoka — `especificacao/001`, e a `especificacao` diz que a do balao "e
        // decisao, nao sugestao". Uso o nome da familia e nao o token CSS porque este valor vai
        // para o Konva, que desenha em canvas e nao resolve `var(--...)`.
        fontFamily: "Fredoka",
        underline: false,
        italic: false,
        bold: false,
      },
      urlDaForma
    );
    newTextBubble.waitImgLoad().then(() => {
      comic.addItem(selectedPageNum, newTextBubble);
      setComic(comic.copy());
    });
  }
/**
 * OS ESTILOS DE QUADRO.
 *
 * T1.23 do pedido: "inserir quadro só tem um estilo de quadro". Era literal — o botão inseria
 * sempre o mesmo retângulo branco de traço fino, sem diálogo e sem escolha.
 *
 * Em quadrinho, o contorno do quadro NÃO é decoração: ele é gramática. Traço grosso e reto é o
 * presente da história; o quadro sem preenchimento deixa a arte de fundo aparecer; o de canto
 * arredondado é convenção de lembrança ou sonho; e o de traço fino serve a quadro pequeno, onde
 * o traço grosso come a arte.
 *
 * Por isso cada opção abaixo tem NOME e EXPLICAÇÃO. Uma paleta de retângulos sem nome obrigaria a
 * criança a descobrir por tentativa; com nome, a escolha ensina alguma coisa sobre quadrinho.
 *
 * Os valores saem do formato de documento v2, que já declarava `traco`, `corTraco`, `corFundo`
 * e `raio` no elemento `quadro` — a capacidade existia no formato e não existia na interface.
 */
/**
 * O TAMANHO REAL DA AREA ONDE A FOLHA CABE.
 *
 * Medido em producao em 10/09: a folha A4 tem 1240x1754 e a area visivel do editor tinha
 * 1079x597. A crianca via cerca de UM TERCO da pagina, e so rolando em dois eixos. E metade do
 * T1.34 do pedido — "as telas achatadas/comprimidas".
 *
 * O observador olha a caixa ELASTICA, nunca aquela cujo tamanho este codigo define. Se olhasse a
 * moldura da folha, leria a propria saida e oscilaria — a licao ja aprendida no
 * `useLarguraDaCaixa` dos motores de leitura (`src/motores/index.tsx`).
 */
function useTamanhoDaCaixa(): [
  React.RefObject<HTMLDivElement>,
  { largura: number; altura: number }
] {
  const caixa = useRef<HTMLDivElement>(null);
  const [tamanho, setTamanho] = useState({ largura: 0, altura: 0 });

  useEffect(() => {
    const el = caixa.current;
    if (!el) return;

    const medir = () => {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        setTamanho({ largura: r.width, altura: r.height });
      }
    };
    medir();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", medir);
      return () => window.removeEventListener("resize", medir);
    }
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, []);

  return [caixa, tamanho];
}

/**
 * OS TAMANHOS DE PÁGINA QUE A TELA OFERECE — T8.8, T8.19 e T8.35.
 *
 * O que havia: "Redimensionar" e "Adicionar página" abriam dois diálogos seguidos — "Digite a nova
 * largura", depois "Digite a nova altura" —, em campo de TEXTO, sem dizer a unidade e sem dizer por
 * que a pergunta existe. Três defeitos medidos, e nenhum é gosto:
 *
 *   1. a altura vinha preenchida com 1240, O MESMO VALOR DA LARGURA, enquanto a A4 do produto tem
 *      1754. Quem aceitasse o que a tela oferecia ganhava uma página QUADRADA — e sem a grade de
 *      quadros do formato escolhido (T8.8);
 *   2. digitar -500 como largura respondia "O comprimento mínimo é de 100": a tela pedia LARGURA e
 *      o erro falava em COMPRIMENTO, e a unidade não aparecia em lugar nenhum (T8.19);
 *   3. pedir pixel a uma criança numa plataforma escolar é pedir o que ela não tem como saber.
 *
 * A SAÍDA NÃO É DIZER A UNIDADE — é parar de falar em pixel, que foi a decisão que a tela de novo
 * quadrinho já tomou e que está medida (seis formatos nomeados, zero ocorrências de "px"). O T8.35
 * pede isso na letra: oferecer "Igual à atual / A4 / Tirinha" EM VEZ DE pedir largura e altura.
 *
 * A LISTA SAI DO `PADROES_PAGINA`, e isso é o ponto: é a MESMA fonte que a tela de criação usa.
 * Duas listas divergiriam no primeiro formato novo, e a criança veria aqui uma opção que não existe
 * lá.
 */
const TAMANHOS_DE_PAGINA = (Object.keys(PADROES_PAGINA) as FormatoPagina[]).map(
  (id) => ({
    id,
    nome: PADROES_PAGINA[id].rotulo,
    explica: PADROES_PAGINA[id].explicacao,
    largura: PADROES_PAGINA[id].largura,
    altura: PADROES_PAGINA[id].altura,
  })
);

const ESTILOS_DE_QUADRO: {
  id: string;
  nome: string;
  explica: string;
  borderWidth: number;
  borderColor: string;
  fillColor: string;
  cornerRadius: number;
}[] = [
  {
    id: "classico",
    nome: "Quadro comum",
    explica: "O traço grosso de HQ. É o que a história usa na maior parte do tempo.",
    borderWidth: 6,
    borderColor: "#000000",
    fillColor: "#ffffff",
    cornerRadius: 0,
  },
  {
    id: "fino",
    nome: "Traço fino",
    explica: "Para quadro pequeno, onde o traço grosso come o desenho.",
    borderWidth: 2,
    borderColor: "#000000",
    fillColor: "#ffffff",
    cornerRadius: 0,
  },
  {
    id: "lembranca",
    nome: "Lembrança",
    explica: "Cantos arredondados. Em quadrinho, é o que marca sonho ou memória.",
    borderWidth: 5,
    borderColor: "#000000",
    fillColor: "#ffffff",
    cornerRadius: 22,
  },
  {
    id: "vazado",
    nome: "Só o contorno",
    explica: "Sem fundo: o que já está desenhado embaixo continua aparecendo.",
    borderWidth: 6,
    borderColor: "#000000",
    fillColor: "transparent",
    cornerRadius: 0,
  },
];

  function addRectangle(estilo = ESTILOS_DE_QUADRO[0]) {
    const newRectangle = new Rectangle({
      id: "",
      // T8.33 · era `x: 20, y: 20` — o canto, por cima da borda do primeiro quadro.
      ...ondeColocar(150, 150),
      width: 150,
      height: 150,
      rotation: 0,
      mirror: false,
      imgId: "",
      borderWidth: estilo.borderWidth,
      borderColor: estilo.borderColor,
      fillColor: estilo.fillColor,
      cornerRadius: estilo.cornerRadius,
    });
    comic.addItem(selectedPageNum, newRectangle);
    setComic(comic.copy());
  }
  function addFigure(imgId: string) {
    const newFigure = new Figure(
      {
        id: "",
        // T8.33 · era `x: 20, y: 20` — o canto, por cima da borda do primeiro quadro.
        ...ondeColocar(150, 150),
        width: 150,
        height: 150,
        rotation: 0,
        mirror: false,
        imgId,
      },
      globalState.appUser?.getImgUrl(imgId) || ""
    );
    newFigure.waitImgLoad().then(() => {
      comic.addItem(selectedPageNum, newFigure);
      setComic(comic.copy());
    });
  }

  const addFunctions = {
    characters: addCharacter,
    scenarios: addScenario,
    textBubbles: addTextBubble,
    rectangles: () => {},
    figures: addFigure,
  };

  const podeDesfazer = pilhaDesfazer.length > 0;

  const desfazer = useCallback(() => {
    if (pilhaDesfazer.length === 0) return;
    const anterior = pilhaDesfazer[pilhaDesfazer.length - 1];

    // AS TRES CHAMADAS FICAM AQUI, LADO A LADO, E NAO DENTRO DO ATUALIZADOR DA PILHA.
    //
    // A primeira versao disto punha `setComic` DENTRO de `setPilhaDesfazer(pilha => ...)`,
    // achando que assim os dois mudariam no mesmo passo. Medido em producao: a pilha
    // encolhia — o botao voltava a ficar desligado — e o quadrinho NAO voltava. Atualizador
    // de estado tem que ser puro; efeito colateral ali nao e garantido, e este foi engolido.
    //
    // O React ja agrupa as tres chamadas de um mesmo manipulador num unico render, entao nao
    // havia nada a ganhar com o aninhamento — so o defeito.
    setPilhaDesfazer((pilha) => pilha.slice(0, -1));
    setComic(anterior);
    // A pagina aberta pode nao existir mais no retrato (desfazer um "Adicionar pagina").
    setSelectedPageNum((n) => Math.min(n, anterior.Pages().length - 1));
  }, [pilhaDesfazer]);

  useEffect(() => {
    const atalho = (e: KeyboardEvent) => {
      // `e.key` e nao `e.code`: em teclado ABNT2 e em Mac o Z esta em lugares diferentes, e o
      // que interessa e a LETRA que a pessoa digitou.
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !e.shiftKey) {
        // Nao rouba o Ctrl+Z de quem esta digitando dentro de um campo.
        const alvo = e.target as HTMLElement | null;
        const etiqueta = alvo?.tagName;
        if (etiqueta === "INPUT" || etiqueta === "TEXTAREA" || alvo?.isContentEditable) {
          return;
        }
        e.preventDefault();
        desfazer();
      }
    };
    window.addEventListener("keydown", atalho);
    return () => window.removeEventListener("keydown", atalho);
  }, [desfazer]);

  const selectedPage = comic.getPage(selectedPageNum);

  // Nunca AMPLIA sozinho: uma tirinha de 1200px esticada para 1600 fica borrada, e o automatico
  // nao deve tomar essa decisao por ninguem. Quem quiser ampliar usa o "+".
  const zoomQueCabe = areaDaFolha.largura
    ? Math.min(
        1,
        areaDaFolha.largura / selectedPage.width,
        areaDaFolha.altura / selectedPage.height
      )
    : 1;
  const zoom = zoomEscolhido ?? zoomQueCabe;

  if (!globalState.appUser) return <></>;

  return (
    <div className="criar-comic-container">
      {isGenerateImgOpen && (
        <GenerateImage
          close={(selectedImageUrl?: string, metadados?: MetadadosDaImagem) => {
            setIsGenerateImgOpen(false);
            if (selectedImageUrl && globalState.appUser) {
              const imageCategories: ElType[] = [
                "characters",
                "scenarios",
                "figures",
              ];
              const target: ElType =
                selectedSubmenu && imageCategories.includes(selectedSubmenu)
                  ? selectedSubmenu
                  : "scenarios";
              const categoryLabels: Record<ElType, string> = {
                characters: "Personagens",
                scenarios: "Cenários",
                figures: "Figuras",
                textBubbles: "Textos",
                rectangles: "Quadros",
              };
              globalState.appUser
                .saveImg(selectedImageUrl, target, metadados)
                .then(() => {
                  setSelectedSubmenu(target);
                  setPopUpInfo({
                    type: "alert",
                    text: `Imagem salva em ${categoryLabels[target]}! Clique nela para adicionar ao canvas.`,
                    close: () => setPopUpInfo(null),
                    confirm: () => setPopUpInfo(null),
                  });
                })
                .catch((err) => {
                  console.error(err);
                  setPopUpInfo({
                    type: "alert",
                    text: "Não deu para guardar essa imagem. Tente de novo daqui a pouco.",
                    close: () => setPopUpInfo(null),
                    confirm: () => setPopUpInfo(null),
                  });
                });
            }
          }}
          user={globalState.appUser}
          setPopUpInfo={setPopUpInfo}
          category={selectedSubmenu}
        />
      )}
      {isImgHistoryOpen && (
        <ImageHistory
          close={(_popupInfo?: PopUpInfo) => {
            setIsImgHistoryOpen(false);

            if (!!_popupInfo) {
              _popupInfo.close = () => {
                setPopUpInfo(null);
              };
              _popupInfo.confirm = () => {
                setPopUpInfo(null);
              };
              setPopUpInfo(_popupInfo);
            }
          }}
          user={globalState.appUser}
        />
      )}
      {popUpInfo && <ConfirmPopUp {...popUpInfo} />}

      {/*comic.renderComic()*/}

      {/* O menu nao precisa mais de gancho: "Meus quadrinhos" e a rota `/meus-quadrinhos`, e
          sair do editor e navegar. O `setCurrentComic(null)` que ficava aqui era o unico jeito
          de voltar para a lista quando as duas telas dividiam um endereco so. */}
      <Nav />
      <div
        id="content-container"
        className="content-container"
        onClick={(e) => {
          e.currentTarget.scroll(0, 0);
        }}
      >
        {/* T24 · CLICAR EM OUTRA COISA DESSELECIONA. O operador: «Se eu cliquei numa outra coisa
            aqui, eu deveria já desselecionar isso aqui, porque fica na minha frente me atrapalhando».
            Captura e não clique: desseleciona antes de qualquer botão do menu abrir a sua tela. */}
        <div className="menu-container" onPointerDownCapture={() => comic.SelectedItem(null)}>
          <div style={{ width: "100%" }}>
            <button
              type="button"
              className="button-container"
              onClick={() => {
                setSelectedSubmenu(
                  selectedSubmenu === "characters" ? null : "characters"
                );
              }}
            >
              <IconePersonagens className="icone-da-ferramenta" />
              <span>Personagens</span>
            </button>

            <button
              type="button"
              className="button-container"
              onClick={() => {
                setSelectedSubmenu(
                  selectedSubmenu === "scenarios" ? null : "scenarios"
                );
              }}
            >
              <IconeCenarios className="icone-da-ferramenta" />
              <span>Cenários</span>
            </button>
            <button
              type="button"
              className="button-container"
              onClick={() => {
                setSelectedSubmenu(
                  selectedSubmenu === "textBubbles" ? null : "textBubbles"
                );
              }}
            >
              <IconeTextos className="icone-da-ferramenta" />
              <span>Textos</span>
            </button>
            <button
              type="button"
              className="button-container"
              onClick={() => {
                setSelectedSubmenu(
                  selectedSubmenu === "figures" ? null : "figures"
                );
              }}
            >
              <IconeFiguras className="icone-da-ferramenta" />
              <span>Figuras</span>
            </button>
            <button
              type="button"
              className="button-container"
              onClick={async () => {
                if (!globalState.appUser)
                  setPopUpInfo({
                    type: "alert",
                    text: "As imagens que você gerou ficam guardadas na sua conta. Entre para ver o histórico.",
                    close: () => setPopUpInfo(null),
                    confirm: () => setPopUpInfo(null),
                  });
                else setIsImgHistoryOpen(true);
              }}
            >
              <IconeHistorico className="icone-da-ferramenta" />
              <span>Histórico</span>
            </button>
            {/* Era `onClick={() => addRectangle()}` numa `div`: um estilo só, sem escolha e sem
                teclado. T1.23 do pedido, na letra. */}
            <button
              type="button"
              className="button-container"
              aria-expanded={mostrarEstilosDeQuadro}
              onClick={() => setMostrarEstilosDeQuadro((x) => !x)}
            >
              <IconeInserirQuadro className="icone-da-ferramenta" />
              <span>Inserir Quadro</span>
            </button>

            {mostrarEstilosDeQuadro && (
              <div className="estilos-de-quadro" role="group" aria-label="Estilo do quadro">
                {ESTILOS_DE_QUADRO.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    className="estilo-de-quadro"
                    onClick={() => {
                      addRectangle(e);
                      setMostrarEstilosDeQuadro(false);
                    }}
                  >
                    <span
                      className="estilo-amostra"
                      aria-hidden="true"
                      style={{
                        borderWidth: Math.max(2, e.borderWidth - 1),
                        borderColor: e.borderColor,
                        background: e.fillColor,
                        borderRadius: e.cornerRadius ? 8 : 0,
                      }}
                    />
                    <span className="estilo-texto">
                      <strong>{e.nome}</strong>
                      <small>{e.explica}</small>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* T2.108 · A RETICULA EM TODAS AS PAGINAS — a metade que faltava.
                A letra dele: "tem que ter uma forma de aplicar reticula EM TODAS AS PAGINAS, se
                quiser, para tentar criar uma UNIFORMIDADE dos desenhos, do traco" — e e o unico
                dos 109 pontos em que ele usa a palavra "essencial".

                O controle que existia era por ITEM, no menu que aparece com a selecao: numa A4 com
                seis quadros e duas paginas, sao doze gestos para conseguir o que ele pediu em um. */}
            <button
              type="button"
              className="button-container"
              aria-expanded={mostrarReticula}
              onClick={() => {
                setMostrarTamanhos(false);
                setMostrarTamanhosNovaPagina(false);
                setMostrarReticula((x) => !x);
              }}
            >
              <IconeRedimensionar className="icone-da-ferramenta" />
              <span style={{ fontSize: "1.1rem" }}>Trama em tudo</span>
            </button>

            {mostrarReticula && (
              <div
                className="estilos-de-quadro"
                role="group"
                aria-label="Trama em todos os quadros"
              >
                <p className="tamanhos-pergunta">
                  Que trama em todos os quadros?
                </p>
                {RETICULAS.map((r) => (
                  <button
                    key={r}
                    type="button"
                    className="estilo-de-quadro"
                    onClick={() => {
                      const antes = comic.copy();
                      const { mudados, quadros, paginas } =
                        comic.aplicarReticulaEmTudo(r, selectedPageNum);
                      setComic(comic.copy());
                      setMostrarReticula(false);
                      // A frase diz O QUE ACONTECEU com numero. "Pronto" nao diz nada a quem
                      // acabou de ver a tela mudar; e quando NADA muda, dizer isso evita a
                      // pessoa clicar de novo achando que nao funcionou.
                      setPopUpInfo({
                        type: "alert",
                        text:
                          quadros === 0
                            ? "Esta história ainda não tem quadro nenhum. Insira um quadro e a trama entra nele."
                            : mudados === 0
                            ? `Os ${quadros} quadros já estavam com essa trama.`
                            : `Trama ${ROTULO_RETICULA[r].toLowerCase()} em ${mudados} de ${quadros} ${
                                quadros === 1 ? "quadro" : "quadros"
                              }, em ${paginas === 1 ? "1 página" : `${paginas} páginas`}.`,
                        close: () => setPopUpInfo(null),
                        confirm: () => setPopUpInfo(null),
                      });
                      void antes;
                    }}
                  >
                    <span
                      className="estilo-amostra amostra-de-trama"
                      aria-hidden="true"
                      style={{
                        // A amostra E a intensidade: mais pontos, trama mais forte. Um retangulo
                        // de cor nao diria a diferenca entre "leve" e "forte".
                        backgroundImage:
                          r === "nenhuma"
                            ? "none"
                            : `radial-gradient(currentColor ${
                                r === "leve" ? 0.7 : r === "media" ? 1 : 1.4
                              }px, transparent 0)`,
                        backgroundSize:
                          r === "nenhuma"
                            ? undefined
                            : `${r === "leve" ? 7 : r === "media" ? 5 : 3}px ${
                                r === "leve" ? 7 : r === "media" ? 5 : 3
                              }px`,
                      }}
                    />
                    <span className="estilo-texto">
                      <strong>{ROTULO_RETICULA[r]}</strong>
                      <small>
                        {r === "nenhuma"
                          ? "Tira a trama de todos os quadros."
                          : "A mesma trama em todos os quadros, de todas as páginas."}
                      </small>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* T8.8 e T8.35 · REDIMENSIONAR DEIXOU DE PEDIR PIXEL.
                Eram dois dialogos em cadeia com campo de texto. Agora e o mesmo gesto da tela de
                criacao: os formatos com nome e motivo, e a folha muda para o que o nome promete. */}
            <button
              type="button"
              className="button-container"
              aria-expanded={mostrarTamanhos}
              onClick={() => {
                setMostrarTamanhosNovaPagina(false);
                setMostrarTamanhos((x) => !x);
              }}
            >
              <IconeRedimensionar className="icone-da-ferramenta" />
              <span style={{ fontSize: "1.1rem" }}>Redimensionar</span>
            </button>

            {mostrarTamanhos && (
              <div
                className="estilos-de-quadro"
                role="group"
                aria-label="Tamanho desta página"
              >
                <p className="tamanhos-pergunta">
                  De que tamanho fica esta página?
                </p>
                {TAMANHOS_DE_PAGINA.map((t) => {
                  const atual =
                    selectedPage.width === t.largura &&
                    selectedPage.height === t.altura;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      className="estilo-de-quadro"
                      aria-current={atual || undefined}
                      onClick={() => {
                        // Redimensionar escreve DIRETO em `selectedPage.width/height`, sem passar
                        // por metodo do `Comic` — entao e o unico lugar em que o retrato precisa
                        // ser tirado a mao. Sem esta linha, "Redimensionar" seria a unica acao da
                        // barra que o desfazer nao alcanca, e um desfazer com um buraco e pior do
                        // que nenhum.
                        registrarAlteracao(comic.copy());
                        selectedPage.width = t.largura;
                        selectedPage.height = t.altura;
                        setComic(comic.copy());
                        setMostrarTamanhos(false);
                      }}
                    >
                      <span
                        className="estilo-amostra amostra-de-folha"
                        aria-hidden="true"
                        style={{
                          // A amostra E a proporcao: 34px no lado maior, e o menor sai da conta.
                          // Assim "deitada" e "rolagem vertical" se distinguem sem ler o texto.
                          width:
                            t.largura >= t.altura
                              ? 34
                              : Math.round((t.largura / t.altura) * 34),
                          height:
                            t.altura >= t.largura
                              ? 34
                              : Math.round((t.altura / t.largura) * 34),
                        }}
                      />
                      <span className="estilo-texto">
                        <strong>
                          {t.nome}
                          {atual ? " · e o desta página" : ""}
                        </strong>
                        <small>{t.explica}</small>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            <button
              type="button"
              className="button-container"
              onClick={() => comic.exportToPdf()}
            >
              <IconeExportarPdf className="icone-da-ferramenta" />
              <span>Exportar para PDF</span>
            </button>
          </div>

          <Submenu
            imgs={globalState.appUser.Imgs()}
            SelectedSubmenu={(value?: ElType | null) => {
              if (value !== undefined) {
                setSelectedSubmenu(value);
                return value;
              } else {
                return selectedSubmenu;
              }
            }}
            addFunctions={addFunctions}
            openGenerateImg={() => {
              if (!globalState.appUser) {
                setPopUpInfo({
                  type: "alert",
                  text: "Para desenhar é preciso entrar — o desenho vai para a sua conta.",
                  close: () => setPopUpInfo(null),
                  confirm: () => setPopUpInfo(null),
                });
              } else {
                // T24 · abrir a janela desseleciona: a barra do item não fica na frente dela.
                comic.SelectedItem(null);
                setIsGenerateImgOpen(true);
              }
            }}
          />
        </div>

        <div className="canva-div">
          <div className="canva-medida" ref={caixaDaFolha}>
            {comic.renderPage(selectedPageNum, {
              zoom,
              aoAlterar: registrarAlteracao,
              // T8.10 · a caixa de dialogo dos ITENS e esta, e nao a da pagina. A pagina remonta a
              // cada mudanca de estado do editor — inclusive a que o proprio clique provoca —, e uma
              // caixa aberta ali e jogada fora antes de aparecer. O porque esta em `renderPage`.
              caixaDoEditor: (info) => setPopUpInfo(info ?? null),
            })}
          </div>

          <div className="zoom-barra" role="group" aria-label="Zoom da página">
            <button
              type="button"
              className="zoom-desfazer"
              onClick={desfazer}
              disabled={!podeDesfazer}
              title={
                podeDesfazer
                  ? "Desfazer a última ação (Ctrl+Z)"
                  : "Nada para desfazer"
              }
            >
              Desfazer
            </button>

            <button
              type="button"
              onClick={() => setZoomEscolhido(Math.max(0.1, zoom - 0.1))}
              aria-label="Diminuir o zoom"
            >
              &minus;
            </button>
            <span className="zoom-valor" aria-live="polite">
              {Math.round(zoom * 100)}%
            </span>
            <button
              type="button"
              onClick={() => setZoomEscolhido(Math.min(3, zoom + 0.1))}
              aria-label="Aumentar o zoom"
            >
              +
            </button>
            <button
              type="button"
              className="zoom-ajustar"
              onClick={() => setZoomEscolhido(null)}
            >
              Caber na tela
            </button>
          </div>

          <div className="comic-menu">
            <button
              type="button"
              className="button"
              onClick={() =>
                setPopUpInfo({
                  type: "confirm",
                  // ── T8.26 · O TEXTO MENTIA SOBRE O QUE O BOTAO FAZ ─────────────────────────
                  //
                  // Ele, na letra: *"'Limpar o quadro' limpa a pagina inteira, nao um quadro."* E e
                  // literal: a funcao chamada e `comic.clearPage(...)`, que apaga TODOS os itens da
                  // pagina aberta. Num editor de quadrinho "quadro" e uma coisa muito especifica —
                  // uma das caixas da pagina —, e prometer apagar uma e apagar seis nao e um texto
                  // infeliz: e um dialogo que consegue a autorizacao errada.
                  //
                  // O espaco antes do "?" tambem sai: nao se escreve assim em portugues, e tres
                  // dialogos deste produto tinham o mesmo espaco.
                  text:
                    "Limpar a página inteira? Todos os quadros, balões e desenhos desta página " +
                    "serão apagados.",
                  rotuloConfirmar: "Limpar a página",
                  close: () => setPopUpInfo(null),
                  confirm: () => {
                    comic.clearPage(selectedPageNum);
                    setComic(comic.copy());
                    setPopUpInfo(null);
                  },
                })
              }
            >
              <IconeLimpar className="icone-da-ferramenta" />
              {/* T8.26 · "Limpar" sozinho nao diz o que limpa, e o que ele limpa e a pagina. */}
              <span>Limpar a página</span>
            </button>

            <div className="page-btns-container">
              {/* Estes dois botoes eram um `<` e um `>` soltos, sem nome acessivel.
                  Um leitor de tela anunciava "botao, menor que" — e mesmo quem enxerga
                  precisava adivinhar que sinal de menor significa "pagina anterior".
                  O sinal continua na tela: e o `aria-label` que diz o que ele faz. */}
              <button
                className="button"
                aria-label="Página anterior"
                title="Página anterior"
                disabled={selectedPageNum === 0}
                onClick={() => setSelectedPageNum(selectedPageNum - 1)}
              >
                <span aria-hidden="true">{"<"}</span>
              </button>

              {/* `gap` no lugar de `space-between`.
                  Esta coluna tem a altura do irmao mais alto da linha, e `space-between`
                  empurrava os dois botoes para as pontas dessa altura — o de baixo saia da
                  propria caixa, e "Remover pagina" aparecia com o texto POR FORA da borda em
                  toda captura de tela que eu tirei do editor. `gap` os mantem colados e deixa
                  cada um com o tamanho que precisa. */}
              <div className="btns-de-pagina">
                {/* Era uma `<div className="button">`: parecia botao, nao era. Sem Tab, sem
                    Enter, sem estado de foco — a mesma doenca das acoes do dashboard. */}
                {/* T8.35 · ADICIONAR PAGINA DEIXOU DE PEDIR PIXEL.
                    "oferecer 'Igual a atual / A4 / Tirinha' EM VEZ DE pedir largura e altura em
                    pixels" — a letra do ponto. "Igual a atual" vem primeiro porque e o que quase
                    sempre se quer: uma historia tem um formato, nao um por pagina. */}
                <button
                  type="button"
                  className="button"
                  aria-expanded={mostrarTamanhosNovaPagina}
                  onClick={() => {
                    setMostrarTamanhos(false);
                    setMostrarTamanhosNovaPagina((x) => !x);
                  }}
                >
                  {/* "Add." e ingles abreviado, e a varredura de portugues nao o pegava
                      justamente por estar abreviado. T1.32 do pedido. */}
                  <span>Adicionar página</span>
                </button>
                <button
                  className="button"
                  disabled={comic.Pages().length === 1}
                  onClick={() => {
                    // T8.31 · Era `window.confirm`, o diálogo do NAVEGADOR — e o produto já tem
                    // modal próprio, usado em "Limpar" e em "Excluir minha conta". O relatório pede
                    // isso na letra: "o site já tem um modal bonito; usá-lo também aqui".
                    //
                    // E não é só aparência: o diálogo do navegador BLOQUEIA a página inteira, some
                    // do fluxo de teclado do produto e não pode dizer QUAL página vai sair. O modal
                    // pode, e é o que a frase abaixo faz — a criança lê o número antes de apagar.
                    setPopUpInfo({
                      type: "confirm",
                      text: `Apagar a página ${selectedPageNum + 1} de ${
                        comic.Pages().length
                      }? O que estiver desenhado nela some.`,
                      // T8.26 · a mesma regra dos outros dois dialogos de apagar: o botao nomeia a
                      // acao. "Sim" responde a uma pergunta que a pessoa pode ter parado de ler, e
                      // este dialogo apaga uma pagina inteira.
                      rotuloConfirmar: "Apagar a página",
                      close: () => setPopUpInfo(null),
                      confirm: () => {
                        if (comic.isLastPage(selectedPageNum)) {
                          setSelectedPageNum(selectedPageNum - 1);
                        }
                        comic.deletePage(selectedPageNum);
                        setComic(comic.copy());
                        setPopUpInfo(null);
                      },
                    });
                  }}
                >
                  <span>Remover página</span>
                </button>

                {/* T8.35 · "Mostrar 'Página 1 de 2' entre as setas".
                    As duas setas existiam sem nada entre elas: dava para andar e nao dava para
                    saber onde se estava, nem quantas paginas a historia tem. `aria-live` porque a
                    contagem muda sem recarregar a tela — sem isso quem usa leitor de tela aperta a
                    seta e nao ouve nada. */}
                <p className="contagem-de-paginas" aria-live="polite">
                  Página {selectedPageNum + 1} de {comic.Pages().length}
                </p>
              </div>

              <button
                className="button"
                aria-label="Próxima página"
                title="Próxima página"
                disabled={comic.isLastPage(selectedPageNum)}
                onClick={() => setSelectedPageNum(selectedPageNum + 1)}
              >
                <span aria-hidden="true">{">"}</span>
              </button>
            </div>

            {mostrarTamanhosNovaPagina && (
              <div
                className="estilos-de-quadro"
                role="group"
                aria-label="Tamanho da nova página"
              >
                <p className="tamanhos-pergunta">
                  De que tamanho vai ser a página nova?
                </p>
                {/* "Igual a atual" primeiro, e e o unico que nao esta no PADROES_PAGINA: ele nao e
                    um formato, e uma copia do que ja esta aberto. Vem antes porque e o que quase
                    sempre se quer — uma historia tem UM formato, nao um por pagina. */}
                <button
                  type="button"
                  className="estilo-de-quadro"
                  onClick={() => {
                    const atual = comic.getPage(selectedPageNum);
                    const n = comic.addPage(atual.width, atual.height);
                    setSelectedPageNum(n - 1);
                    setComic(comic.copy());
                    setMostrarTamanhosNovaPagina(false);
                  }}
                >
                  <span
                    className="estilo-amostra amostra-de-folha"
                    aria-hidden="true"
                    style={{
                      width:
                        selectedPage.width >= selectedPage.height
                          ? 34
                          : Math.round(
                              (selectedPage.width / selectedPage.height) * 34
                            ),
                      height:
                        selectedPage.height >= selectedPage.width
                          ? 34
                          : Math.round(
                              (selectedPage.height / selectedPage.width) * 34
                            ),
                    }}
                  />
                  <span className="estilo-texto">
                    <strong>Igual à atual</strong>
                    <small>
                      O mesmo formato desta página. É o que quase sempre se
                      quer.
                    </small>
                  </span>
                </button>
                {TAMANHOS_DE_PAGINA.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className="estilo-de-quadro"
                    onClick={() => {
                      const n = comic.addPage(t.largura, t.altura);
                      setSelectedPageNum(n - 1);
                      setComic(comic.copy());
                      setMostrarTamanhosNovaPagina(false);
                    }}
                  >
                    <span
                      className="estilo-amostra amostra-de-folha"
                      aria-hidden="true"
                      style={{
                        width:
                          t.largura >= t.altura
                            ? 34
                            : Math.round((t.largura / t.altura) * 34),
                        height:
                          t.altura >= t.largura
                            ? 34
                            : Math.round((t.altura / t.largura) * 34),
                      }}
                    />
                    <span className="estilo-texto">
                      <strong>{t.nome}</strong>
                      <small>{t.explica}</small>
                    </span>
                  </button>
                ))}
              </div>
            )}

            <button
              type="button"
              className="button"
              onClick={() => {
                const userId = globalState.appUser?.Id();
                if (!userId) return;

                if (!comic.isSaved()) {
                  setPopUpInfo({
                    type: "input",
                    text: "Digite o nome do quadrinho",
                    close: () => setPopUpInfo(null),
                    confirm: (comicName) => {
                      comicName = comicName.trim();

                      // T8.31 · os três últimos `alert()` vivos desta tela viraram o modal do
                      // produto. E as frases mudaram junto: "Nome de quadrinho inválido" não diz o
                      // que fazer, e "passou de 30 caracteres" fala em caracteres — uma criança
                      // conta letras.
                      const avisar = (texto: string) =>
                        setPopUpInfo({
                          type: "alert",
                          text: texto,
                          close: () => setPopUpInfo(null),
                          confirm: () => setPopUpInfo(null),
                        });
                      if (!comicName)
                        return avisar("Dê um nome ao seu quadrinho para poder guardá-lo.");
                      if (comicName.length > 30)
                        return avisar(
                          `O nome ficou com ${comicName.length} letras, e cabem 30. Escolha um mais curto.`
                        );
                      // if (input in comics) return alert("Já existe um quadrinho com esse nome");
                      comic
                        .save(userId, comicName)
                        .then(() => {
                          setPopUpInfo({
                            type: "alert",
                            text: "Quadrinho salvo!",
                            close: () => setPopUpInfo(null),
                            confirm: () => setPopUpInfo(null),
                          });
                          setComic(comic.copy());
                          // T8.20 · agora ele tem id, e a URL pode deixar de dizer "rascunho".
                          const novoId = comic.getId();
                          if (novoId) aoGanharId?.(novoId);
                        })
                        .catch((err) => {
                          console.log(err);
                          avisar(
                            "Não deu para guardar o quadrinho agora. O seu desenho continua aqui — tente de novo daqui a pouco."
                          );
                        });
                      // setCurrentComic(comicName)

                      // O CAMINHO ANTIGO DO SALVAR SAIU DAQUI, em 26/09.
                      //
                      // Eram dois blocos comentados com a versao que o `comic.save()` acima
                      // substituiu -- 64 linhas de codigo morto que duplicavam o caminho vivo,
                      // com `alert()` do navegador, `setComics` que nao existe mais e
                      // `setPopUpInfo(undefined)` onde o tipo pede `null`.
                      //
                      // Sairam porque codigo comentado nao executa e ninguem le -- mas ELE MENTE:
                      // o teste do T8.31, que proibe dialogo do navegador, teve que aprender a
                      // ignorar bloco comentado e ainda assim falhou, porque o regex de comentario
                      // casa ate o PRIMEIRO `*/` e havia dois blocos. O historico esta no git.
                    },
                  });
                } else {
                  setPopUpInfo({
                    type: "confirm",
                    text: `Salvar ${comic.Name()}?`,
                    close: () => setPopUpInfo(null),
                    confirm: () => {
                      comic
                        .save(userId)
                        .then(() => {
                          setPopUpInfo({
                            type: "alert",
                            text: "Quadrinho salvo!",
                            close: () => setPopUpInfo(null),
                            confirm: () => setPopUpInfo(null),
                          });
                          setComic(comic.copy());
                        })
                        .catch((err) => {
                          console.log(err);
                          // T8.31 · o ramo do quadrinho que JA TEM NOME tambem passou pelo modal.
                          // Ele estava escondido num `else` e sobreviveu a primeira varredura.
                          setPopUpInfo({
                            type: "alert",
                            text: "Não deu para guardar o quadrinho agora. O seu desenho continua aqui — tente de novo daqui a pouco.",
                            close: () => setPopUpInfo(null),
                            confirm: () => setPopUpInfo(null),
                          });
                        });
                    },
                  });
                }
              }}
            >
              <IconeSalvar className="icone-da-ferramenta" />
              <span>Salvar</span>
            </button>

            {/* T8.28 · "mostrar 'Salvo há 5 s'".
                Fica logo embaixo do botão Salvar, porque é a resposta à pergunta que o botão
                levanta. `aria-live="polite"` para quem usa leitor de tela ouvir a mudança sem
                precisar ir procurar; `polite` e não `assertive` porque isto não interrompe nada.
                `title` com a frase inteira porque a régua é estreita. */}
            <ReguaDoSalvamento salvamento={salvamento} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateComic;
